// A painted board flattened into one picture (docs/186): the painter's ground with everything that stands up
// laid over it back to front (by foot), for screens that show a fight's ground without the Phaser scene: the
// epilogue and the other staged backdrops (`ui/screens/intro/Scene.tsx`) and the opening harness. No light
// map, fog or units here: those screens draw their own figures over the picture.
import type { EncounterSet, PaintedBoard, Piece, Standing } from './types';

export interface CompositeOptions {
  /**
   * Px of room kept above row 0 for what rises over it (crowns, raised walls); anything higher is cut.
   * Defaults to the board's own `headroom`.
   */
  top?: number;
  /** Board px y above which the ground is left clear (a horizon row that keeps only a lip under its trunks). */
  groundFrom?: number;
}

/** The flattened picture: `w × h` RGBA, the board's row 0 at `top` px down. */
export interface Composite { w: number; h: number; top: number; px: Uint8ClampedArray }

/** Every file of the set by its frame name. */
const frameIndex = new WeakMap<EncounterSet, Map<string, Piece>>();
function framesOf(set: EncounterSet): Map<string, Piece> {
  let m = frameIndex.get(set);
  if (!m) {
    m = new Map();
    for (const list of set.pieces.values()) for (const p of list) m.set(p.frame, p);
    frameIndex.set(set, m);
  }
  return m;
}

/** A standing piece's own pixels: its painted `px`, or its set file's. */
function pixelsOf(st: Standing, frames: Map<string, Piece>): Uint8ClampedArray | undefined {
  if (st.px) return st.px;
  const p = st.frame ? frames.get(st.frame) : undefined;
  return p && p.w === st.w && p.h === st.h ? p.px : undefined;
}

/** Lay straight-alpha `src` (`sw` px a row) over `dst` at (dx, dy), mirrored when `flipX`; clipped to `dst`. */
function over(dst: Uint8ClampedArray, dw: number, dh: number, src: Uint8ClampedArray, sw: number, sh: number, dx: number, dy: number, flipX = false): void {
  for (let v = 0; v < sh; v++) {
    const y = dy + v;
    if (y < 0 || y >= dh) continue;
    for (let u = 0; u < sw; u++) {
      const x = dx + u;
      if (x < 0 || x >= dw) continue;
      const si = (v * sw + (flipX ? sw - 1 - u : u)) * 4;
      const a = src[si + 3]!;
      if (!a) continue;
      const di = (y * dw + x) * 4;
      if (a === 255) { dst[di] = src[si]!; dst[di + 1] = src[si + 1]!; dst[di + 2] = src[si + 2]!; dst[di + 3] = 255; continue; }
      const sa = a / 255, ba = dst[di + 3]! / 255, o = sa + ba * (1 - sa);
      for (let k = 0; k < 3; k++) dst[di + k] = (src[si + k]! * sa + dst[di + k]! * ba * (1 - sa)) / o;
      dst[di + 3] = o * 255;
    }
  }
}

/** The painted board's ground with its standing pieces over it, sorted by foot (flipX honoured). */
export function compositeBoard(painted: PaintedBoard, set: EncounterSet, opts: CompositeOptions = {}): Composite {
  const top = Math.max(0, Math.round(opts.top ?? painted.headroom));
  const gw = painted.w * painted.cell, gh = painted.h * painted.cell;
  const w = gw, h = gh + top;
  const px = new Uint8ClampedArray(w * h * 4);
  const from = Math.max(0, opts.groundFrom ?? 0);
  // the ground, row for row (it is opaque where it is drawn; under a horizon only its lip)
  for (let y = from; y < gh; y++) px.set(painted.ground.subarray(y * gw * 4, (y + 1) * gw * 4), (y + top) * w * 4);
  const frames = framesOf(set);
  const order = [...painted.standing].sort((a, b) => a.foot - b.foot || a.y - b.y || a.x - b.x);
  for (const st of order) {
    const src = pixelsOf(st, frames);
    if (src) over(px, w, h, src, st.w, st.h, st.x, st.y + top, st.flipX);
  }
  return { w, h, top, px };
}

/** The composite on a canvas (OffscreenCanvas where there is one), to `drawImage` from. */
export function compositeCanvas(c: Composite): HTMLCanvasElement | OffscreenCanvas | undefined {
  const canvas: HTMLCanvasElement | OffscreenCanvas | undefined = typeof OffscreenCanvas !== 'undefined'
    ? new OffscreenCanvas(c.w, c.h)
    : typeof document !== 'undefined' ? Object.assign(document.createElement('canvas'), { width: c.w, height: c.h }) : undefined;
  const ctx = canvas?.getContext('2d') as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null | undefined;
  if (!canvas || !ctx) return undefined;
  ctx.putImageData(new ImageData(new Uint8ClampedArray(c.px), c.w, c.h), 0, 0);
  return canvas;
}
